/**
 * @consenteer/sdk — public entry point.
 * Inspired by silktide/consent-manager (cf. docs/decisions.md ADR-010).
 *
 * Two ways to use the SDK on a site:
 *
 * 1) Drop-in via <script>:
 *      <script id="csteer"
 *              src="https://cdn.consenteer.com/lib/csteer.js"
 *              data-site="abc" defer></script>
 *    The SDK auto-init's from `data-site`. The API base URL is derived
 *    from the script tag's own `src` origin — so dropping the snippet
 *    anywhere works without any additional configuration. An explicit
 *    `data-api="..."` override is accepted when needed.
 *
 * 2) Programmatic:
 *      import { init } from "@consenteer/sdk";
 *      init({ siteId: "abc", apiUrl: "...", onAccept: (...)=> ... });
 *
 * Conflict surface:
 *   - The IIFE bundle wraps everything in a closure and exposes only
 *     `window.csteer`. No other globals leaked.
 *   - GCM v2 declares `window.dataLayer` and `window.gtag` defensively
 *     (`= window.dataLayer ?? []`) — won't clobber an existing GTM setup.
 *   - The banner DOM is mounted inside a Shadow DOM (closed style scope),
 *     so the host site's CSS cannot leak in and our styles cannot leak out.
 *   - No window-level event listeners on resize/scroll/etc.
 */

export {
  init,
  update,
  openPreferences,
  resetConsent,
  getConsentChoice,
  getAcceptedConsents,
  getInstance,
} from "./api.js";
export { CONSENT_EVENT } from "./events.js";
export type { ConsentEventDetail, ConsentEventSource } from "./events.js";
export type {
  CsteerConfig,
  CsteerInstance,
  ConsentChoices,
  BannerSettings,
  WebstatEvent,
} from "./types.js";

import { init } from "./api.js";
import { armKnownAutoBlock } from "./auto-block-known.js";
import { isConsenteerHost, warnSelfHostedCopy } from "./origin.js";

/**
 * Synchronously arm the generic auto-block at SDK load time — BEFORE any vendor
 * bootstrap injects its script — when the cached settings for this site say the
 * feature is on. First-ever visit has no cache (can't block parse-time scripts);
 * repeat visits arm early. The async init() re-confirms against fresh settings.
 */
function armEarlyFromCache(siteId: string): void {
  try {
    const raw = localStorage.getItem(`csteer:settings:${siteId}`);
    if (!raw) return;
    const cached = JSON.parse(raw);
    if (cached?.settings?.compliance?.autoBlockKnown === true) armKnownAutoBlock();
  } catch {
    // Ignore malformed cache / unavailable storage.
  }
}

/**
 * Derive the API base URL from the SDK script's own src.
 *
 * In dev the saas-api serves the script itself, so the script origin IS the
 * API origin. In production the script is served from the CDN host
 * (cdn.consenteer.com) while the API lives on its sibling saas.consenteer.com —
 * the CDN vhost only proxies /lib/ and /logo/, NOT /v1/. So a `cdn.` script
 * origin is remapped to its `saas.` sibling. `data-api` overrides this.
 *
 * ⚠️ Only when the script really comes from us. A copy re-hosted on the
 * customer's own domain by an optimisation plugin says nothing about where the
 * API lives: return undefined so `init()` falls back to the canonical API
 * (cf. origin.ts).
 */
function inferApiUrlFromScript(script: HTMLScriptElement): string | undefined {
  if (!script.src) return undefined;
  try {
    const url = new URL(script.src);
    if (!isConsenteerHost(url.hostname)) {
      warnSelfHostedCopy(url.hostname);
      return undefined;
    }
    url.hostname = url.hostname.replace(/^cdn\./, "saas.");
    return url.origin;
  } catch {
    return undefined;
  }
}

// Auto-init from script tag attributes when running in a browser.
// We try, in order:
//   1. document.currentScript — null with `defer` but works for inline use
//   2. document.getElementById("csteer") — the recommended snippet now
//      includes id="csteer" so this is the fast & precise lookup
//   3. any <script data-site="..."> — fallback for legacy snippets
if (typeof document !== "undefined") {
  const byId = document.getElementById("csteer");
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    (byId instanceof HTMLScriptElement ? byId : null) ??
    document.querySelector<HTMLScriptElement>("script[data-site]");
  if (script instanceof HTMLScriptElement) {
    const siteId = script.dataset.site;
    // `data-api` takes precedence (escape hatch); otherwise use the script's
    // own origin, which is the natural choice when the SDK is hosted on the
    // same domain as the saas-api (e.g. saas.consenteer.com).
    const apiUrl = script.dataset.api ?? inferApiUrlFromScript(script);
    if (siteId) {
      armEarlyFromCache(siteId);
      void init({ siteId, apiUrl });
    }
  }
}
